/**
 * Auth-related TypeScript types and interfaces
 * Based on Liberty Rent API documentation
 */

/**
 * User role information
 * Based on /api/verify endpoint response structure
 */
export interface UserRole {
  role_id: number
  org_id?: number
  org_name?: string
  org_logo?: string
  org_swoosh_logo?: string
  org_website?: string
  role_name: string
  company_id?: number | null
  property_id?: number | null
  company_name?: string | null
  property_name?: string | null
  company_logo?: string | null
  property_logo?: string | null
  fin_mgr_attribute?: string | null
  company_listed?: string | null
  property_listed?: string | null
  com_listed?: string | null
  prop_listed?: string | null
  sales_role?: string | null
  charity_reference?: boolean
  finance_manager?: boolean
  /** System announcements from change_role — drives the acknowledgement popup. */
  notification?: Array<{
    sa_id: number
    announcement_topic: string
    announcement_description?: string
    start_date?: string
    end_date?: string
    show_effective_dates?: string | number | boolean
    isshown?: string | number | boolean
  }>
  report_url?: string | null
  tiles?: string[] | Record<string, unknown> | { dashboard_tiles?: string[] }
  policy?: string[]
  actions?: unknown[]
}

/**
 * User roles mapping
 */
export interface UserRoles {
  [key: string]: UserRole
}

/**
 * User data from API response
 * Based on /api/verify endpoint response structure
 */
export interface UserData {
  user_id: number
  first_name: string
  middle_name?: string | null
  last_name: string
  email: string
  mobile_no?: string
  received_notification_email?: string | null
  show_tab_indication?: string | null
  force_password_change?: string
  training_date_acknowledged?: string | null
  training_response?: string | null
  training_asked_count?: number
  job_title?: string | null
  roles?: UserRoles
  current_role?: UserRole
  profile_pic?: string
  otp_verified?: boolean
  ip_address?: string
}

/**
 * Login request payload
 */
export interface LoginRequest {
  email: string
  password: string
  captcha_token?: string
}

/**
 * Login response from API
 */
export interface LoginResponse {
  success: boolean
  token: string
  user_data: UserData
  message?: string
  ott?: string
}

/**
 * Registration request payload
 */
export interface RegisterRequest {
  email: string
  confirm_email: string
  first_name: string
  middle_name?: string
  last_name: string
  mobile_no: string
  shared_phone_no: string
  password: string
  confirm_password: string
  birthdate?: string
  // Apply page specific fields
  invite_code?: string
  is_web?: boolean
}

/**
 * Registration response from API
 */
export interface RegisterResponse {
  success: boolean
  message: string
  data?: {
    email: string
    first_name: string
    last_name: string
    user_id: number
  }
  token?: string
}

/**
 * Invite data from invitation lookup (apply-now flow)
 */
export interface InviteData {
  ai_id?: number
  first_name?: string
  middle_name?: string
  last_name?: string
  email?: string
  mobile_no?: string
  birthdate?: string
  invite_accepted?: string
  rent_amount?: number
  unit_number?: string
  property_id?: number
  lease_start_date?: string
  lease_duration?: string
  fk_user_id?: number
  allow_to_movein?: string | null
  deleted?: string | null
  created_at?: number
  fk_progress_status_id?: number
  status?: string
  co_applicant?: string | null
  property_name?: string
  resubmit_invite?: string | null
  approval_needed?: string | null
  invite_status?: string
}

/**
 * Auth state for Redux
 */
export interface AuthState {
  user: UserData | null
  token: string | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  inviteData: InviteData | null
  currentInviteId: string | null
}

/**
 * Required role for renter
 */
export const ROLE_RENTER = 'ROLE_RENTER'

/**
 * Admin role constant
 */
export const ROLE_ADMIN = 'ROLE_ADMIN'

/**
 * Change role request payload
 */
export interface ChangeRoleRequest {
  role_id: number
  otp?: string
}

/**
 * Change role response from API
 */
export interface ChangeRoleResponse {
  allow_role_change: boolean
  message: string
  otp_required?: boolean
  notification?: Array<{
    sa_id: number
    announcement_topic: string
    announcement_description?: string
    start_date?: string
    end_date?: string
    show_effective_dates?: string | number | boolean
    isshown?: string | number | boolean
  }>
  report_url?: string
  tiles?: {
    dashboard_tiles?: string[]
  }
}

/**
 * Check if user has renter role
 * @param userData - User data from API
 * @returns True if user has ROLE_RENTER
 */
export function hasRenterRole(userData: UserData): boolean {
  // Check current_role first
  if (userData.current_role?.role_name === ROLE_RENTER) {
    return true
  }

  // If no current_role, check if user has ONLY ROLE_RENTER
  if (!userData.current_role && userData.roles) {
    const roleKeys = Object.keys(userData.roles)
    return roleKeys.length === 1 && roleKeys[0] === ROLE_RENTER
  }

  return false
}

/**
 * Check if user has admin role
 * @param userData - User data from API
 * @returns True if user has ROLE_ADMIN
 */
export function hasAdminRole(userData: UserData): boolean {
  // Check current_role first
  if (userData.current_role?.role_name === ROLE_ADMIN) {
    return true
  }

  // Check if user has ROLE_ADMIN in roles
  if (userData.roles) {
    return Object.values(userData.roles).some(
      (role) => role.role_name === ROLE_ADMIN
    )
  }

  return false
}

/**
 * Get available roles from user data
 * @param userData - User data from API
 * @returns Array of available roles
 */
export function getAvailableRoles(userData: UserData): UserRole[] {
  if (!userData.roles) return []
  return Object.values(userData.roles)
}

/**
 * Check if user has multiple roles (excluding Liberty internal roles)
 * @param userData - User data from API
 * @returns True if user has multiple non-Liberty roles
 */
export function hasMultipleRoles(userData: UserData): boolean {
  if (!userData.roles) return false
  
  const roles = Object.values(userData.roles)
  // Filter out Liberty internal roles if needed
  const nonInternalRoles = roles.filter(
    (role) => !role.role_name.includes('LIBERTY_INTERNAL')
  )
  
  return nonInternalRoles.length > 1
}

